import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { Typography } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';

import {
  useTQConnectProfileReviews,
} from '../../../../hooks/queries/tqConnectQueries/user';
import { numFormatterInUnit } from '../../../../shared/formatter';
import {
  ITQProfileReviewsStatistics,
  IUserReviewsFilters,
} from '../../../../types/tqConnectTypes/user';
import { UserAccountType } from '../../../../types/userTypes';
import UserTabItemTypeSelector from '../UserTabItemTypeSelector';
import NoReviews from './NoReviews';
import ReviewCard from './ReviewCard';
import ReviewCardSkeleton from './ReviewCardSkeleton';
import ReviewsFilters from './ReviewsFilters';

interface Props {
  userProfileId: string;
  userName: string;
  viewerType: UserAccountType;
  filters: IUserReviewsFilters;
  onChangeFilters: (
    key: keyof IUserReviewsFilters,
    value: IUserReviewsFilters[keyof IUserReviewsFilters],
  ) => void;
  isViewerSelf?: boolean;
  reviewReceived: number;
  reviewGiven: number;
}

const UserReviews: React.FC<Props> = ({
  userProfileId,
  userName,
  viewerType,
  filters,
  onChangeFilters,
  isViewerSelf,
  reviewReceived,
  reviewGiven,
}) => {
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const {
    data: userReviewsResult,
    isLoading: loading,
    hasNextPage: hasMore,
    fetchNextPage,
    isFetchingNextPage,
  } = useTQConnectProfileReviews({
    queryForUser: userProfileId,
    type: filters.type,
    ...(filters?.rating && { rating: filters.rating }),
  });

  const { list, total, stats } = useMemo(() => {
    return {
      list: userReviewsResult?.data ?? [],
      total: userReviewsResult?.total ?? 0,
      stats: (userReviewsResult?.statistics ??
        {}) as ITQProfileReviewsStatistics,
    };
  }, [
    userReviewsResult?.data,
    userReviewsResult?.total,
    userReviewsResult?.statistics,
  ]);

  const loadMore = () => {
    if (hasMore && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    // const element = document.getElementById(USER_COMMUNITY_POSTS_SCROLL);
    const element = document.getElementsByClassName(
      'ant-tabs-content-holder',
    )[0] as HTMLElement;

    if (element) {
      scrollContainerRef.current = element;
    }
  }, [userProfileId]);

  const renderSkeletons = (length: number) => {
    return (
      <>
        {Array.from({ length }).map((_, index) => (
          <ReviewCardSkeleton key={`review-card-skeleton-${index}`} />
        ))}
      </>
    );
  };

  return (
    <div className="tab__reviews">
      <div className="tab__reviews__header">
        <Typography.Title level={4} className="header__title">
          {viewerType === 'creator' || isViewerSelf
            ? 'Reviews'
            : `Reviews received ${reviewReceived ? `(${numFormatterInUnit(reviewReceived)})` : ''}`}
        </Typography.Title>

        {viewerType === 'creator' || isViewerSelf ? (
          <UserTabItemTypeSelector
            item="Reviews"
            selectedType={filters.type}
            onSelectType={(type) => onChangeFilters('type', type)}
            receivedCount={reviewReceived}
            givenCount={reviewGiven}
          />
        ) : null}
      </div>

      {/* {total === 0 && !loading && !hasMore && !filters.rating ? null : ( */}
      <ReviewsFilters
        selectedRating={filters.rating}
        onSelectRating={(rating) => onChangeFilters('rating', rating)}
        stats={stats}
      />
      {/* )} */}

      <div className="review__list">
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          hasMore={hasMore}
          useWindow={false}
          getScrollParent={() => scrollContainerRef.current}
          loader={
            (!loading && (hasMore || isFetchingNextPage)
              ? renderSkeletons(2)
              : null) as JSX.Element
          }>
          {loading ? renderSkeletons(2) : null}

          {list.map((review, index) => (
            <ReviewCard
              key={`service-request-review-${review.givenBy?._id}-${index}`}
              review={review}
            />
          ))}

          {total > 0 && !loading && !hasMore ? (
            <Typography.Paragraph
              style={{
                width: '100%',
                textAlign: 'center',
                marginBlock: 32,
              }}>
              That’s it! No more reviews {filters.type}.
            </Typography.Paragraph>
          ) : null}
        </InfiniteScroll>

        {total === 0 && !loading && !hasMore ? (
          <NoReviews
            userName={userName}
            viewerType={viewerType}
            isViewerSelf={isViewerSelf}
            filters={filters}
          />
        ) : null}
      </div>
    </div>
  );
};

export default memo(UserReviews);
